import { FormStep } from "../formModels";
import {
  IAboutYouFormInformationModel,
  IAboutYouFormPhotoModel,
  IMainAndYPFormInformationModel,
  CurrentMemberBlockPropsType,
} from "../formModels/AboutYouFormModel";
import {
  EligibilitySteps,
  JoiningType,
  ICreditFormModel,
  ICourseFormModel,
  IRecommendationFormModel,
} from "../formModels/EligibilityFormModel";
import {
  INITIAL_CREDIT_FORM_VALUES,
  INITIAL_COURSE_FORM_VALUES,
  INITIAL_FORM_VALUES,
} from "../formModels/initialFormValues";
import { IReviewFormModel } from "../formModels/ReviewFormModel";
import {
  AppStageId,
  IOnlineApplication,
} from "../services/online-application.types";
import { YOUNG_PERFORMER_TYPE_CODE } from "../constants/PerformerType";

const initialAboutYouValues =
  INITIAL_FORM_VALUES[FormStep.AboutYou].aboutYouInformationForm;
const initialCurrentMemberValues =
  INITIAL_FORM_VALUES[FormStep.AboutYouMPCurrentMember];
const initialHeadshotValues =
  INITIAL_FORM_VALUES[FormStep.AboutYou].photoUploadForm;
const initialReviewValues = INITIAL_FORM_VALUES[FormStep.Review];

const eligibilityInitialValues = INITIAL_FORM_VALUES[FormStep.Eligibility];
const eligibilitySteps =
  eligibilityInitialValues[EligibilitySteps.AdditionalInformation];
const initialCredits = eligibilitySteps[JoiningType.Experienced];
const initialCourses = eligibilitySteps[JoiningType.Training];
const initialRecommendation = eligibilitySteps[JoiningType.Recommendation];

const JoiningAsMap = new Map([
  ["Eligibility", JoiningType.Experienced],
  ["Training", JoiningType.Training],
  ["Recommendation", JoiningType.Recommendation],
]);

export const convertStringToDate = (dateString?: string) => {
  try {
    if (!dateString) return;

    const date = new Date(dateString);

    return date;
  } catch (error: any) {
    console.error(error);

    // return undefined when date cannot be parsed correctly
    return undefined;
  }
};

export const getJoiningAsData = (onlineApplication: IOnlineApplication) => {
  const joiningAsResult = JoiningAsMap.get(onlineApplication.pathway);

  return joiningAsResult !== undefined ? joiningAsResult : null;
};

export const getCourseData = (
  onlineApplication: IOnlineApplication
): ICourseFormModel[] => {
  if (!onlineApplication.courses || onlineApplication.courses.length === 0)
    return initialCourses;

  return onlineApplication.courses.map((returnedCourse) => {
    let { courseType } = INITIAL_COURSE_FORM_VALUES;

    if (returnedCourse.courseType === "fullTime") courseType = "yes";

    if (returnedCourse.courseType === "partTime") courseType = "no";

    return {
      schoolName:
        returnedCourse.schoolName || INITIAL_COURSE_FORM_VALUES.schoolName,
      courseName:
        returnedCourse.courseName || INITIAL_COURSE_FORM_VALUES.courseName,
      dateFrom:
        convertStringToDate(returnedCourse.dateFrom) ||
        INITIAL_COURSE_FORM_VALUES.dateFrom,
      dateTo:
        convertStringToDate(returnedCourse.dateTo) ||
        INITIAL_COURSE_FORM_VALUES.dateTo,
      qualificationLevel:
        returnedCourse.qualificationLevel ||
        INITIAL_COURSE_FORM_VALUES.qualificationLevel,
      courseUrl:
        returnedCourse.courseUrl || INITIAL_COURSE_FORM_VALUES.courseUrl,
      s3ObjectKey:
        returnedCourse.s3ObjectKey || INITIAL_COURSE_FORM_VALUES.s3ObjectKey,
      courseType,
    };
  });
};

export const getCreditData = (
  onlineApplication: IOnlineApplication
): ICreditFormModel[] => {
  if (!onlineApplication.credits || onlineApplication.credits.length === 0)
    return initialCredits;

  return onlineApplication.credits.map((returnedCredit) => ({
    productionName:
      returnedCredit.productionName ||
      INITIAL_CREDIT_FORM_VALUES.productionName,
    type: returnedCredit.type || INITIAL_CREDIT_FORM_VALUES.type,
    roleName: returnedCredit.roleName || INITIAL_CREDIT_FORM_VALUES.roleName,
    company: returnedCredit.company || INITIAL_CREDIT_FORM_VALUES.company,
    director: returnedCredit.director || INITIAL_CREDIT_FORM_VALUES.director,
    creditLink:
      returnedCredit.creditLink || INITIAL_CREDIT_FORM_VALUES.creditLink,
    dateFrom:
      convertStringToDate(returnedCredit.dateFrom) ||
      INITIAL_CREDIT_FORM_VALUES.dateFrom,
    dateTo:
      convertStringToDate(returnedCredit.dateTo) ||
      INITIAL_CREDIT_FORM_VALUES.dateTo,
    s3ObjectKey:
      returnedCredit.s3ObjectKey || INITIAL_CREDIT_FORM_VALUES.s3ObjectKey,
    equityRole:
      returnedCredit.equityRole || INITIAL_CREDIT_FORM_VALUES.equityRole,
  }));
};

export const getRecommendationData = (
  onlineApplication: IOnlineApplication
): IRecommendationFormModel => ({
  refereeName:
    onlineApplication.recommendation?.refereeName ||
    initialRecommendation.refereeName,
  refereeMembership:
    onlineApplication.recommendation?.refereeMembership ||
    initialRecommendation.refereeMembership,
  s3ObjectKey:
    onlineApplication.recommendation?.s3ObjectKey ||
    initialRecommendation.s3ObjectKey,
});

export const getHeadshotData = (
  onlineApplication: IOnlineApplication
): IAboutYouFormPhotoModel => ({
  photo: onlineApplication.photograph?.photoKey || initialHeadshotValues.photo,
  photographerName:
    onlineApplication.photograph?.photographerName ||
    initialHeadshotValues.photographerName,
});

export const getCurrentMemberData = (
  onlineApplication: IOnlineApplication
): CurrentMemberBlockPropsType => ({
  currentMember:
    onlineApplication.currentMember || initialCurrentMemberValues.currentMember,
  currentName:
    onlineApplication.currentName || initialCurrentMemberValues.currentName,
  currentPerformerType:
    onlineApplication.appearingNowPublicationCode ||
    initialCurrentMemberValues.currentPerformerType,
  currentEmail:
    onlineApplication.currentEmail || initialCurrentMemberValues.currentEmail,
});

export const getAboutYouData = (
  onlineApplication: IOnlineApplication,
  isYP?: boolean
): IAboutYouFormInformationModel | IMainAndYPFormInformationModel => {
  const commonData = {
    currentMember:
      onlineApplication.currentMember || initialAboutYouValues.currentMember,
    currentName:
      onlineApplication.currentName || initialAboutYouValues.currentName,
    currentPerformerType:
      onlineApplication.appearingNowPublicationCode ||
      initialAboutYouValues.currentPerformerType,
    currentEmail:
      onlineApplication.currentEmail || initialAboutYouValues.currentEmail,
    publishAge:
      onlineApplication.publishAge || initialAboutYouValues.publishAge,
    playingAgeFrom:
      onlineApplication.playingAgeFrom || initialAboutYouValues.playingAgeFrom,
    playingAgeTo:
      onlineApplication.playingAgeTo || initialAboutYouValues.playingAgeTo,
    gender: onlineApplication.gender || initialAboutYouValues.gender,
    customOrNonBinaryGender:
      onlineApplication.customOrNonBinaryGender ||
      initialAboutYouValues.customOrNonBinaryGender,
    furtherInformation:
      onlineApplication.furtherInformation ||
      initialAboutYouValues.furtherInformation,
    showGenderOnProfile:
      onlineApplication.showGenderOnProfile ||
      initialAboutYouValues.showGenderOnProfile,
    deafOrDisabled:
      onlineApplication.deafOrDisabled || initialAboutYouValues.deafOrDisabled,
    street1: onlineApplication?.address?.street1,
    street2: onlineApplication?.address?.street2,
    city: onlineApplication?.address?.city,
    postal_code: onlineApplication?.address?.postal_code,
    country: onlineApplication?.address?.country,
    state_province: onlineApplication?.address?.state_province,
  };

  return isYP
    ? commonData
    : {
        ...commonData,
        mobileNumber:
          onlineApplication.mobileNumber || initialAboutYouValues.mobileNumber,
        landlineNumber:
          onlineApplication.landlineNumber ||
          initialAboutYouValues.landlineNumber,
        dateOfBirth:
          convertStringToDate(onlineApplication.dateOfBirth) ||
          initialAboutYouValues.dateOfBirth,
        membershipOptions:
          onlineApplication.membershipOptions ||
          initialAboutYouValues.membershipOptions,
        agents:
          onlineApplication.agents?.filter((id) => id !== 50) || // filtering out the default agent of id 50
          initialAboutYouValues.agents,
      };
};

export const getReviewData = (
  onlineApplication: IOnlineApplication,
  isCD?: boolean
): IReviewFormModel => {
  const { marketing } = onlineApplication;
  const { emailPreferences, phone, sms, thirdParties } = marketing;
  const returnedPrefs = [phone, sms].filter((val) => val);

  if (isCD) {
    return {
      emailPreferences:
        emailPreferences || initialReviewValues.emailPreferences,
    };
  }

  return {
    preferences: returnedPrefs || initialReviewValues.preferences,
    emailPreferences: emailPreferences || initialReviewValues.emailPreferences,
    thirdParties: thirdParties || initialReviewValues.thirdParties,
  };
};

const ApplicationStageMap = (performerType?: number) => {
  const appStageMap = new Map([
    [AppStageId.Welcome, 0], // Go to pathway selection page
    [AppStageId.Pathway, 0], // Go to pathway selection page
    [AppStageId.Eligibility, 1], // Go to eligibility step
    [AppStageId.AboutYou, 2], // Go to review page
    [AppStageId.Review, 2], // Go to review page
  ]);

  if (performerType === YOUNG_PERFORMER_TYPE_CODE) {
    appStageMap.set(AppStageId.AboutYou, 1);
    appStageMap.set(AppStageId.Review, 1);
  }

  return appStageMap;
};

export const getApplicationStep = (onlineApplication: IOnlineApplication) => {
  const appStep = ApplicationStageMap(onlineApplication.performerType).get(
    onlineApplication.completedPageId
  );

  const isPathwayChosen =
    onlineApplication.pathway && onlineApplication.pathway !== null;

  return {
    mainStep: appStep || 0, // refers to the page the user is on
    subStep: isPathwayChosen ? 1 : 0, // refers to eligibility sub step
  };
};
